import React, { useContext } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import { useSelector } from 'react-redux';
import { Checkbox, FormControlLabel, FormHelperText, Grid, Tooltip, Box, Button, IconButton } from '@mui/material';
import { Help as HelpIcon, PlayCircleOutline as PlayCircleOutlineIcon, Clear as ClearIcon } from '@mui/icons-material';
import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment';
import DaysOfWeekSelector from './DaysSelectors/DaysOfWeekSelector';
import DonationContext from '../../../../context/DonationContext/DonationContext';
import DaysOfMonthSelector from './DaysSelectors/DaysOfMonthSelector/DaysOfMonthSelector';
import {
  FREQUENCY_EVERY_OTHER_WEEK,
  FREQUENCY_MONTHLY,
  FREQUENCY_ONCE,
  FREQUENCY_WEEKLY,
} from '../../../../models/donationsNew';
import { DONOR_LINK_PAUSED_TOOLTIP, donationManageActions } from '../../../../helpers/donations';
import { Colors } from '../../../../assets/theme/Colors';
import FrequencySelector from './Frequency/FrequencySelector';
import { DONATION_TYPE_REQUEST } from '../../../../actions/donationNew';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';

const Schedule = ({ disableWeekly = false, disableMonthly = false, disableEveryOtherWeek = false, receiverPreselected = false, isDonorLinkDonation = false, isPausedByDonorLink = false }) => {
  const {
    frequency: frequency,
    frequency_every: frequencyEvery,
    days_of_week: daysOfWeek,
    days_of_month: daysOfMonth,
    start_date: startDate,
    end_date: endDate,
    pause_date: pauseDate,
    resume_date: resumeDate,
  } = useSelector(state => state.ui.donation_new.draft);
  const { start_date: originalStartDate } = useSelector(state => state.ui.donation_new.original);

  const { errors, onFieldChange, isMobileView, isNew, isLoading, donationType } = useContext(DonationContext);

  const handleDaysOfWeekChange = event => {
    const { checked, value } = event.target;
    const valueInt = parseInt(value, 10);

    if (checked) {
      return onFieldChange(donationManageActions.add_day_of_week, valueInt);
    }

    return onFieldChange(donationManageActions.remove_day_of_week, valueInt);
  };

  const handleDaysOfMonthChange = ({ newOrdinaryValue, newDayValue, oldOrdinaryValue, oldDayValue }) => {
    if (newOrdinaryValue === null && newDayValue === null && oldOrdinaryValue && (oldDayValue || oldDayValue === 0)) {
      return onFieldChange(donationManageActions.remove_day_of_month, {
        day: oldDayValue,
        ordinal: oldOrdinaryValue,
      });
    }

    if (
      newOrdinaryValue &&
      (newDayValue || newDayValue === 0) &&
      !oldOrdinaryValue &&
      !oldDayValue &&
      oldDayValue !== 0
    ) {
      return onFieldChange(donationManageActions.add_day_of_month, {
        day: newDayValue,
        ordinal: newOrdinaryValue,
      });
    }

    if (newDayValue !== oldDayValue && newOrdinaryValue === oldOrdinaryValue) {
      return onFieldChange(donationManageActions.set_day_of_month_day, {
        day: oldDayValue,
        ordinal: oldOrdinaryValue,
        newDay: newDayValue,
      });
    }

    if (newDayValue === oldDayValue && newOrdinaryValue !== oldOrdinaryValue) {
      return onFieldChange(donationManageActions.set_day_of_month_ordinal, {
        day: oldDayValue,
        ordinal: oldOrdinaryValue,
        newOrdinal: newOrdinaryValue,
      });
    }
  };

  const minDonationDate = (() => {
    if (DONATION_TYPE_REQUEST === donationType) {
      return moment().add(1, 'day');
    }

    if (isNew) {
      return moment();
    }

    if (moment().isBefore(originalStartDate.value)) {
      return moment();
    }

    return moment(originalStartDate.value);
  })();

  return (
    <Grid container spacing={2}>
      <Grid container item>
        <Grid item xs={12}>
          <FrequencySelector
            disabled={isLoading}
            frequency={frequency}
            frequencyEvery={frequencyEvery}
            isDonorLinkDonation={isDonorLinkDonation}
            onChange={(value) => {
              if (receiverPreselected && frequency === FREQUENCY_ONCE) {
                return confirmAlert({
                  title: 'Confirm change to frequency',
                  message: `Changing the frequency of this donation will clear the receiving agency that has been selected. You will have to reselect ${receiverPreselected.name}, or a different receiving agency to continue.`,
                  buttons: [
                    {
                      label: 'Cancel',
                      color: 'primary',
                      variant: 'outlined',
                    },
                    {
                      label: 'Confirm',
                      color: 'primary',
                      onClick: () => {
                        onFieldChange(donationManageActions.set_frequency, value);
                      },
                    },
                  ],
                  customUI: ({ title, message, onClose, buttons }) => (
                    <ConfirmationDialog
                      buttons={buttons}
                      closeDialog={onClose}
                      title={title}
                      message={message}
                    />
                  ),
                });
              }
              return onFieldChange(donationManageActions.set_frequency, value);
            }}
            disableWeekly={disableWeekly}
            disableMonthly={disableMonthly}
            disableEveryOtherWeek={disableEveryOtherWeek}
          />
        </Grid>
      </Grid>
      {(frequency === FREQUENCY_WEEKLY || frequency === FREQUENCY_EVERY_OTHER_WEEK) && (
        <Grid container item>
          <Grid item xs={12}>
            <DaysOfWeekSelector
              disabled={isLoading}
              dow={daysOfWeek}
              onChange={handleDaysOfWeekChange}
              error={
                (daysOfWeek || []).length === 0 ? { message: 'You must select at least 1 day of the week' } : false
              }
            />
          </Grid>
        </Grid>
      )}
      {frequency === FREQUENCY_MONTHLY && (
        <Grid container item>
          <Grid item xs={12}>
            <DaysOfMonthSelector disabled={isLoading} dom={daysOfMonth} onChange={handleDaysOfMonthChange} />
          </Grid>
        </Grid>
      )}

      <Grid container item spacing={2}>
        <Grid container item spacing={2}>
          <Grid container item md={12} lg={8} xl={6} spacing={2}>
            <Grid item xs={12} md={6}>
              <Box
                display="flex"
                flexDirection="row"
                flexWrap="no-wrap"
                alignItems="center"
                gap="8px"
                justifyContent="space-between"
              >
                <Box display="flex" flexDirection="column">
                  <DatePicker
                    disabled={
                      isLoading || frequency === FREQUENCY_ONCE
                        ? moment(originalStartDate.value, originalStartDate.format).isBefore(moment(), 'day')
                        : false
                    }
                    minDate={minDonationDate}
                    value={moment(startDate.value)}
                    onChange={momentObject => {
                      const stringDate = momentObject.format('YYYY-MM-DD');
                      onFieldChange(donationManageActions.set_start_date, stringDate);
                    }}
                    label={frequency === FREQUENCY_ONCE ? 'Date' : 'Start Date'}
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        size: isMobileView ? 'medium' : 'small',
                        fullWidth: true,
                        placeholder: 'mm/dd/yyyy',
                        error: !!errors.start_date,
                        inputProps: {
                          'data-testid': 'start_date',
                        },
                      },
                    }}
                  />
                  {DONATION_TYPE_REQUEST === donationType && (
                    <FormHelperText error={!!errors.start_date}>
                      Pickup window must be requested at least 24 hours in advance. For pickups in the next 24 hours,
                      please contact your Site Director
                    </FormHelperText>
                  )}
                </Box>
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    frequency === FREQUENCY_ONCE
                      ? 'Rescue will occur on this date.'
                      : 'Rescues will begin after this date.'
                  }
                >
                  <HelpIcon htmlColor={Colors.blue.main} />
                </Tooltip>
              </Box>
            </Grid>
            {frequency !== FREQUENCY_ONCE && (
              <Grid item xs={12} md={6}>
                <Box display="flex" flexDirection="row" flexWrap="no-wrap" alignItems="center" gap="8px">
                  <DatePicker
                    disabled={isLoading}
                    minDate={moment(startDate.value)}
                    value={endDate ? moment(endDate.value) : null}
                    onChange={momentObject => {
                      const stringDate = momentObject.format('YYYY-MM-DD');
                      onFieldChange(donationManageActions.set_end_date, stringDate);
                    }}
                    label="End Date"
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        size: isMobileView ? 'medium' : 'small',
                        fullWidth: true,
                        placeholder: 'mm/dd/yyyy',
                        error: !!errors.start_date,
                        inputProps: {
                          'data-testid': 'start_date',
                        },
                      },
                    }}
                  />
                  {endDate && (
                    <IconButton size="small" onClick={() => onFieldChange(donationManageActions.set_end_date, null)}>
                      <ClearIcon />
                    </IconButton>
                  )}
                  <Tooltip
                    arrow
                    placement="top"
                    title="Rescues will end after this date. Entering an end date is optional."
                  >
                    <HelpIcon htmlColor={Colors.blue.main} />
                  </Tooltip>
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
        {frequency !== FREQUENCY_ONCE && (
          <Grid container item spacing={2}>
            <Grid container item md={12} lg={8} xl={6} spacing={2}>
              <Grid item xs={12} sm={6}>
                {pauseDate ? (
                  <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="no-wrap"
                    alignItems="center"
                    gap="8px"
                    justifyContent="space-between"
                  >
                    <DatePicker
                      disabled={isLoading || isPausedByDonorLink}
                      minDate={moment(startDate.value)}
                      maxDate={endDate ? moment(endDate.value) : undefined}
                      value={pauseDate ? moment(pauseDate.value) : null}
                      format="MM/DD/YYYY"
                      label="Pause after date"
                      onChange={momentObject => {
                        const stringDate = momentObject.format('YYYY-MM-DD');
                        onFieldChange(donationManageActions.set_pause_date, stringDate);
                      }}
                      slotProps={{
                        textField: {
                          variant: 'outlined',
                          size: isMobileView ? 'medium' : 'small',
                          fullWidth: true,
                          placeholder: 'mm/dd/yyyy',
                          error: !!pauseDate && (
                            moment(pauseDate.value).isBefore(moment(startDate.value)) ||
                            (endDate && moment(pauseDate.value).isAfter(moment(endDate.value)))
                          ),
                          helperText: !!pauseDate && moment(pauseDate.value).isBefore(moment(startDate.value))
                            ? 'Pause date should be after start date'
                            : !!pauseDate && endDate && moment(pauseDate.value).isAfter(moment(endDate.value))
                            ? 'Pause date should be before end date'
                            : '',
                          inputProps: {
                            'data-testid': 'start_date',
                          },
                        },
                      }}
                    />
                    <Tooltip arrow placement="top" title="Rescue will pause after this date.">
                      <HelpIcon htmlColor={Colors.blue.main} />
                    </Tooltip>
                  </Box>
                ) : (
                  <Button
                    disabled={isLoading}
                    color="secondary"
                    startIcon={<PlayCircleOutlineIcon />}
                    variant="contained"
                    onClick={() =>
                      onFieldChange(
                        donationManageActions.set_pause_date,
                        (moment().isBefore(startDate.value) ? moment(startDate.value) : moment()).format('YYYY-MM-DD')
                      )
                    }
                  >
                    Pause now
                  </Button>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="no-wrap"
                  alignItems="center"
                  gap="8px"
                  justifyContent="space-between"
                >
                  <Tooltip arrow placement="top" title={isPausedByDonorLink ? DONOR_LINK_PAUSED_TOOLTIP : ''}>
                    <span>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={isLoading || isPausedByDonorLink}
                            color="secondary"
                            onChange={event => {
                              // when unchecked & there was pauseDate before, clear it
                              if (event.target.checked) {
                                // make sure that pause date is after start date (eg. when donation is from the future)
                                return onFieldChange(
                                  donationManageActions.set_pause_date,
                                  (moment().isBefore(startDate.value) ? moment(startDate.value) : moment()).format(
                                    'YYYY-MM-DD'
                                  )
                                );
                              } else {
                                onFieldChange(donationManageActions.set_pause_date, null);
                              }
                            }}
                            checked={!!pauseDate}
                            name="pause_date"
                          />
                        }
                        label="Pause after date"
                      />
                    </span>
                  </Tooltip>
                  <Tooltip arrow placement="top" title="Rescue will pause after this date.">
                    <HelpIcon htmlColor={Colors.blue.main} />
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )}
        {!!pauseDate && (
          <Grid container item spacing={2}>
            <Grid container item md={12} lg={8} xl={6} spacing={2}>
              <Grid item xs={12} sm={6}>
                {resumeDate ? (
                  <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="no-wrap"
                    alignItems="center"
                    gap="8px"
                    justifyContent="space-between"
                  >
                    <DatePicker
                      disabled={isLoading || isPausedByDonorLink}
                      minDate={moment(pauseDate.value)}
                      minDateMessage="Resume date should be after pause date"
                      maxDate={endDate ? moment(endDate.value) : undefined}
                      maxDateMessage="Resume date should be before end date"
                      value={resumeDate ? moment(resumeDate.value) : null}
                      format="MM/DD/YYYY"
                      label="Resume on date"
                      onChange={momentObject => {
                        const stringDate = momentObject.format('YYYY-MM-DD');
                        onFieldChange(donationManageActions.set_resume_date, stringDate);
                      }}
                      slotProps={{
                        textField: {
                          variant: 'outlined',
                          size: isMobileView ? 'medium' : 'small',
                          fullWidth: true,
                          error:
                            resumeDate && moment(resumeDate.value).isBefore(moment(pauseDate.value))
                              ? true
                              : resumeDate && endDate && moment(resumeDate.value).isAfter(moment(endDate.value))
                              ? true
                              : false,
                          helperText:
                            resumeDate && moment(resumeDate.value).isBefore(moment(pauseDate.value))
                              ? 'Resume date should be after pause date'
                              : resumeDate && endDate && moment(resumeDate.value).isAfter(moment(endDate.value))
                              ? 'Resume date should be before end date'
                              : '',
                        },
                      }}
                    />
                    <Tooltip arrow placement="top" title="Rescue will resume on this date.">
                      <HelpIcon htmlColor={Colors.blue.main} />
                    </Tooltip>
                  </Box>
                ) : (
                  <Tooltip arrow placement="top" title={isPausedByDonorLink ? DONOR_LINK_PAUSED_TOOLTIP : ''}>
                    <span>
                      <Button
                        disabled={isLoading || isPausedByDonorLink}
                        color="secondary"
                        startIcon={<PlayCircleOutlineIcon />}
                        variant="contained"
                        onClick={() => {
                          // make sure that resume date is after start date (eg. when donation is from the future)
                          return onFieldChange(
                            donationManageActions.set_resume_date,
                            (moment().isBefore(startDate.value) ? moment(startDate.value) : moment()).format('YYYY-MM-DD')
                          );
                        }}
                      >
                        Resume now
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="no-wrap"
                  alignItems="center"
                  gap="8px"
                  justifyContent="space-between"
                >
                  <Tooltip arrow placement="top" title={isPausedByDonorLink ? DONOR_LINK_PAUSED_TOOLTIP : ''}>
                    <span>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={isLoading || isPausedByDonorLink}
                            color="secondary"
                            onChange={event => {
                              if (event.target.checked) {
                                // make sure that resume date is after start date (eg. when donation is from the future)
                                return onFieldChange(
                                  donationManageActions.set_resume_date,
                                  (moment().isBefore(startDate.value) ? moment(startDate.value) : moment()).format(
                                    'YYYY-MM-DD'
                                  )
                                );
                              } else {
                                onFieldChange(donationManageActions.set_resume_date, null);
                              }
                            }}
                            checked={!!resumeDate}
                            name="resume_date"
                          />
                        }
                        label="Resume on date"
                      />
                    </span>
                  </Tooltip>
                  <Tooltip arrow placement="top" title="Rescue will resume on this date.">
                    <HelpIcon htmlColor={Colors.blue.main} />
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default Schedule;
